import { useCallback, useEffect, useRef, useState } from "react";

import { hasConfirmedIdentityDenial } from "./templateEditorRecovery";

const RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000] as const;
const FALLBACK_RATE_LIMIT_MS = 8_000;
const MAX_TIMER_DELAY_MS = 2_147_483_647;
const DEFINITE_IDENTITY_ERROR_CODES = new Set([
  "AUTH_SESSION_INVALID",
  "AUTH_TOKEN_MISSING",
  "AUTH_ACCOUNT_DISABLED",
  "AUTH_SESSION_REVOKED",
  "AUTH/USER-TOKEN-EXPIRED",
  "AUTH/USER-DISABLED",
  "AUTH/USER-NOT-FOUND",
]);

interface ReconnectState {
  scopeKey: string;
  attempts: number;
  error: unknown;
  retryNotBefore: number;
  retryBlocked: boolean;
  timerWake: number;
}

const getIdentityErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object" || !("status" in error))
    return undefined;
  return typeof error.status === "number" ? error.status : undefined;
};

const getRetryAfterMs = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object" || !("retryAfterMs" in error))
    return undefined;
  const value = error.retryAfterMs;
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.min(value, Number.MAX_SAFE_INTEGER)
    : undefined;
};

const getRetryAfterAt = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object" || !("retryAfterAt" in error))
    return undefined;
  const value = error.retryAfterAt;
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.min(value, Number.MAX_SAFE_INTEGER)
    : undefined;
};

const hasDefiniteIdentityFailureCode = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const record = error as Record<string, unknown>;
  const data =
    record.data && typeof record.data === "object"
      ? (record.data as Record<string, unknown>)
      : undefined;
  const response =
    record.response && typeof record.response === "object"
      ? (record.response as Record<string, unknown>)
      : undefined;
  const responseData =
    response?.data && typeof response.data === "object"
      ? (response.data as Record<string, unknown>)
      : undefined;
  return [
    record.code,
    record.errorCode,
    data?.code,
    data?.errorCode,
    responseData?.code,
    responseData?.errorCode,
  ].some(
    (code) =>
      typeof code === "string" &&
      DEFINITE_IDENTITY_ERROR_CODES.has(code.toUpperCase()),
  );
};

/** A definite session failure needs a new sign-in, not another identity read. */
export const isDefiniteIdentitySessionError = (error: unknown): boolean =>
  getIdentityErrorStatus(error) === 401 ||
  Boolean(
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "status" in error.response &&
    error.response.status === 401,
  ) ||
  hasDefiniteIdentityFailureCode(error);

/** Retain an already allowed route only for the same actor's non-denial recheck. */
export const useIdentityReconnect = ({
  scopeKey,
  allowedNow,
  isError,
  isFetching,
  error,
  refetch,
}: {
  scopeKey: string | null;
  allowedNow: boolean;
  isError: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => unknown;
}) => {
  const allowedScope = useRef<string | null>(null);
  const activeRetry = useRef<{ scopeKey: string; token: symbol } | null>(null);
  const [retryInFlightScope, setRetryInFlightScope] = useState<string | null>(
    null,
  );
  const [reconnect, setReconnect] = useState<ReconnectState | null>(null);
  const status = getIdentityErrorStatus(error);
  const definiteCode = hasConfirmedIdentityDenial(error);
  const retryable =
    !definiteCode &&
    (status === 408 ||
      status === 429 ||
      (status !== undefined && status >= 500 && status <= 599));
  const otherClientError =
    !definiteCode &&
    status !== undefined &&
    status >= 400 &&
    status <= 499 &&
    status !== 401 &&
    status !== 403 &&
    !retryable;
  const retainable = isError && (retryable || otherClientError);
  const denied = isError && !retainable;
  const mayRetain = Boolean(
    scopeKey && allowedScope.current === scopeKey && retainable,
  );
  const retryAfterAt = status === 429 ? getRetryAfterAt(error) : undefined;
  const retryAfterMs = status === 429 ? getRetryAfterMs(error) : undefined;
  const retryInFlight = Boolean(scopeKey && retryInFlightScope === scopeKey);
  const startRefetch = useCallback((): boolean => {
    if (!scopeKey || activeRetry.current?.scopeKey === scopeKey) return false;
    const token = Symbol("identity-retry");
    activeRetry.current = { scopeKey, token };
    setRetryInFlightScope(scopeKey);
    const complete = () => {
      if (activeRetry.current?.token !== token) return;
      activeRetry.current = null;
      setRetryInFlightScope(null);
    };
    try {
      void Promise.resolve(refetch()).then(complete, complete);
    } catch {
      complete();
    }
    return true;
  }, [refetch, scopeKey]);

  // CRITICAL: only an already-authorized actor and route may retain their UI
  // after an indeterminate or non-denial check. 401/403, local auth loss and
  // actor/scope changes fail closed; writes still require server authorization.
  // https://redux-toolkit.js.org/rtk-query/usage/queries
  useEffect(() => {
    if (
      !scopeKey ||
      allowedScope.current !== scopeKey ||
      denied ||
      (!isError && !isFetching && !allowedNow)
    ) {
      allowedScope.current = null;
    }
    if (scopeKey && allowedNow && !isError && !isFetching) {
      allowedScope.current = scopeKey;
    }
  }, [allowedNow, denied, isError, isFetching, scopeKey]);

  useEffect(() => {
    if (scopeKey && mayRetain) {
      setReconnect((current) => {
        if (current?.scopeKey === scopeKey && current.error === error)
          return current;
        const now = Date.now();
        const retryNotBefore =
          retryAfterAt ??
          Math.min(
            Number.MAX_SAFE_INTEGER,
            now +
              (retryAfterMs ?? (status === 429 ? FALLBACK_RATE_LIMIT_MS : 0)),
          );
        return {
          scopeKey,
          attempts: current?.scopeKey === scopeKey ? current.attempts : 0,
          error,
          retryNotBefore,
          retryBlocked: retryNotBefore > now,
          timerWake: 0,
        };
      });
    } else if (!isFetching) {
      setReconnect((current) => (current ? null : current));
    }
  }, [
    error,
    isFetching,
    mayRetain,
    retryAfterAt,
    retryAfterMs,
    scopeKey,
    status,
  ]);

  // CRITICAL: only the idempotent identity read is retried, at most four times.
  // Never retry before Retry-After, including when the delay exceeds a native
  // timer's range. Saves and other mutations are not retried here.
  // https://www.rfc-editor.org/rfc/rfc9110.html#name-retry-after
  useEffect(() => {
    if (
      !mayRetain ||
      !retryable ||
      !scopeKey ||
      reconnect?.scopeKey !== scopeKey ||
      reconnect.error !== error ||
      isFetching ||
      retryInFlight
    )
      return;
    const remaining = Math.max(0, reconnect.retryNotBefore - Date.now());
    const exhausted = reconnect.attempts >= RETRY_DELAYS_MS.length;
    if (exhausted && remaining === 0) return;
    const delay = exhausted
      ? remaining
      : Math.max(RETRY_DELAYS_MS[reconnect.attempts], remaining);
    const timer = window.setTimeout(
      () => {
        if (Date.now() < reconnect.retryNotBefore) {
          setReconnect((current) =>
            current?.scopeKey === scopeKey
              ? { ...current, timerWake: current.timerWake + 1 }
              : current,
          );
          return;
        }
        const started = exhausted ? false : startRefetch();
        setReconnect((current) =>
          current?.scopeKey === scopeKey
            ? {
                ...current,
                attempts: started ? current.attempts + 1 : current.attempts,
                retryBlocked: false,
              }
            : current,
        );
      },
      Math.min(delay, MAX_TIMER_DELAY_MS),
    );
    return () => window.clearTimeout(timer);
  }, [
    error,
    isFetching,
    mayRetain,
    reconnect,
    retryInFlight,
    retryable,
    scopeKey,
    startRefetch,
  ]);

  const retaining =
    mayRetain ||
    Boolean(
      scopeKey &&
      allowedScope.current === scopeKey &&
      isFetching &&
      reconnect?.scopeKey === scopeKey,
    );
  const retriesRemain =
    reconnect?.scopeKey !== scopeKey ||
    reconnect.error !== error ||
    reconnect.attempts < RETRY_DELAYS_MS.length;
  const reconnecting =
    retaining && (isFetching || retryInFlight || (retryable && retriesRemain));
  const retryOffered = retaining && (retryable || isFetching || retryInFlight);
  const retryAllowed = Boolean(
    mayRetain &&
    retryable &&
    !isFetching &&
    !retryInFlight &&
    (status !== 429 ||
      (reconnect && reconnect.error === error && !reconnect.retryBlocked)),
  );
  const retryNow = () => {
    if (!retryAllowed) return;
    if (
      reconnect &&
      reconnect.error === error &&
      Date.now() < reconnect.retryNotBefore
    )
      return;
    startRefetch();
  };
  return {
    retaining,
    reconnecting,
    retryOffered,
    retryAllowed,
    retryNow,
    error: mayRetain ? error : reconnect?.error,
  };
};
